import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { stockSyncApi } from '@/lib/api/stock-sync.api';

import type {
  CreateStockSourceDto,
  StockSourceQuery,
  UpdateStockSourceDto,
} from '@repo/shared-types';

const KEY = ['stock-sources'];

export function useStockSources(query?: StockSourceQuery) {
  return useQuery({
    queryKey: [...KEY, query],
    queryFn: () => stockSyncApi.getAll(query),
  });
}

export function useStockSource(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'one', id],
    queryFn: () => stockSyncApi.getOne(id!),
    enabled: !!id,
  });
}

export function useCreateStockSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStockSourceDto) => stockSyncApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateStockSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStockSourceDto }) =>
      stockSyncApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteStockSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => stockSyncApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useTestStockSource() {
  return useMutation({
    mutationFn: (data: Parameters<typeof stockSyncApi.test>[0]) => stockSyncApi.test(data),
  });
}

export function useTriggerStockSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => stockSyncApi.triggerSync(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCancelStockSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => stockSyncApi.cancelSync(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useEnrichStockImages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, all }: { id: string; all?: boolean }) => stockSyncApi.enrichImages(id, all),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Sync-state polls while a sync or enrichment is running so progress is live. */
export function useStockSyncState(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'state', id],
    queryFn: () => stockSyncApi.syncState(id!),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.running || query.state.data?.enriching_images ? 5000 : false,
  });
}

export function useStockSyncRuns(id: string | undefined, page = 1) {
  return useQuery({
    queryKey: [...KEY, 'runs', id, page],
    queryFn: () => stockSyncApi.runs(id!, page),
    enabled: !!id,
  });
}

export function useStockItems(id: string | undefined, page = 1, search?: string) {
  return useQuery({
    queryKey: [...KEY, 'items', id, page, search],
    queryFn: () => stockSyncApi.items(id!, page, 20, search),
    enabled: !!id,
  });
}
